import type * as React from 'react'
import { createContext, useContext } from 'react'
import type { ApolloError } from '@apollo/client'

import type {
  ChannelType,
  FeatureChannels,
} from '~publish/pages/Comments/types'
import type { Service } from '~publish/gql/graphql'
import { useSplitEnabled } from '@buffer-mono/features'

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const THREADS_PROFILE = { service: 'threads' as Service, type: ['profile'] }
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const FACEBOOK_PAGE = { service: 'facebook' as Service, type: ['page'] }
const INSTAGRAM_BUSINESS = {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  service: 'instagram' as Service,
  type: ['business'],
}
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const BLUESKY_PROFILE = { service: 'bluesky' as Service, type: ['profile'] }

const MOCK_COMMENTS_CONFIG = (
  isBlueskyCommentsEnabled: boolean,
): {
  supportedChannelsByFeature: FeatureChannels[]
} => {
  // When Facebook comments are enabled:
  // - Facebook pages are supported in comments feature
  // - Facebook pages are removed from engage feature
  const commentsChannels = [
    THREADS_PROFILE,
    FACEBOOK_PAGE,
    INSTAGRAM_BUSINESS,
    ...(isBlueskyCommentsEnabled ? [BLUESKY_PROFILE] : []),
  ]

  return {
    supportedChannelsByFeature: [
      {
        feature: 'comments',
        supportedChannels: commentsChannels,
      },
      {
        feature: 'engage',
        supportedChannels: [],
      },
    ],
  }
}

export type CommentsConfig = {
  isSupportedInComments: (channel: ChannelType) => boolean
  isSupportedInEngage: (channel: ChannelType) => boolean
  isChannelSupported: (channel: ChannelType) => boolean
  hasSupportedCommentChannel: (channels: ChannelType[]) => boolean
}

type ChannelConfig = {
  config: CommentsConfig
  loading: boolean
  error: ApolloError | null
}

const CommentsChannelConfigContext = createContext<ChannelConfig | undefined>(
  undefined,
)

function isChannelSupportedInFeature(
  channel: ChannelType,
  featureConfig: FeatureChannels | undefined,
): boolean {
  if (!featureConfig) return false
  return featureConfig.supportedChannels.some(
    (supportedChannel) =>
      supportedChannel.service === channel.service &&
      supportedChannel.type.includes(channel.type),
  )
}

function CommentsChannelConfigProvider({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const { isEnabled: isBlueskyCommentsEnabled } = useSplitEnabled(
    'geid-cet-bluesky-comments',
  )

  // When API is ready, replace with useQuery
  const featureConfigs = MOCK_COMMENTS_CONFIG(
    isBlueskyCommentsEnabled,
  ).supportedChannelsByFeature
  const commentsConfig = featureConfigs.find((c) => c.feature === 'comments')
  const engageConfig = featureConfigs.find((c) => c.feature === 'engage')

  // Helper functions
  const isSupportedInComments = (channel: ChannelType): boolean =>
    isChannelSupportedInFeature(channel, commentsConfig)
  const isSupportedInEngage = (channel: ChannelType): boolean =>
    isChannelSupportedInFeature(channel, engageConfig)

  const value = {
    config: {
      isSupportedInComments,
      isSupportedInEngage,
      isChannelSupported: (channel: ChannelType): boolean =>
        isSupportedInComments(channel) || isSupportedInEngage(channel),
      hasSupportedCommentChannel: (channels: ChannelType[]): boolean =>
        channels.some(isSupportedInComments),
    },
    loading: false,
    error: null,
  }

  return (
    <CommentsChannelConfigContext.Provider value={value}>
      {children}
    </CommentsChannelConfigContext.Provider>
  )
}

function useCommentsConfig(): ChannelConfig {
  const context = useContext(CommentsChannelConfigContext)

  if (!context) {
    throw new Error('useCommentsConfig must be used within CommentsProvider')
  }

  return context
}

export { useCommentsConfig, CommentsChannelConfigProvider }
